<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-vertical="end"
        >
            <DCol
                sm="12"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    class="float-end"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <CollectionFilter
            v-model:invoice-id="filter.invoiceId"
            v-model:customer-id="filter.customerId"
            v-model:customer-search="filter.customerSearch"
            v-model:invoice-price-operator="filter.invoicePriceOperator"
            v-model:invoice-price="filter.invoicePrice"
            v-model:selected-brands="filter.brands"
            v-model:exclude-invoices-with-open-incidents="filter.excludeInvoicesWithOpenIncidents"
            v-model:currency="filter.currency"
            :brands="brands"
            :currency-codes="currencyCodes"
            @filter="applyFilter"
        />
        <DTable
            class="table-responsive table-sticky-header"
            :items="invoiceList"
            :fields="getFields()"
        >
            <template #collection-header>
                <div class="header-actions">
                    {{ __('tools.collection.headers.collection') }}
                    <DFormCheck
                        id="header_collection"
                        v-model="headerActions.collection"
                        inline
                        type="radio"
                        :name="'header'"
                        @click="onClickAllActions('collection')"
                    />
                </div>
            </template>
            <template #debt_loss-header>
                <div class="header-actions">
                    {{ __('tools.collection.headers.debt_loss') }}
                    <DFormCheck
                        id="header_debt_loss"
                        v-model="headerActions.debt_loss"
                        inline
                        type="radio"
                        :name="'header'"
                        @click="onClickAllActions('debt_loss')"
                    />
                </div>
            </template>
            <template #collection_and_debt_loss-header>
                <div class="header-actions">
                    {{ __('tools.collection.headers.collection_and_debt_loss') }}
                    <DFormCheck
                        id="header_collection_and_debt_loss"
                        v-model="headerActions.collection_and_debt_loss"
                        inline
                        type="radio"
                        :name="'header'"
                        @click="onClickAllActions('collection_and_debt_loss')"
                    />
                </div>
            </template>
            <template #ignore-header>
                <div class="header-actions">
                    {{ __('tools.in_lawsuit.headers.ignore') }}
                    <DFormCheck
                        id="ignore"
                        v-model="headerActions.ignore"
                        inline
                        type="radio"
                        :name="'header'"
                        @click="onClickAllActions('ignore')"
                    />
                </div>
            </template>

            <template #collection="{item}">
                <td>
                    <DFormCheck
                        :id="`action_${item.id}collection`"
                        v-model="item.collection"
                        inline
                        type="radio"
                        :name="item.id"
                        @change="onChangeActions(item, 'collection')"
                    />
                </td>
            </template>
            <template #debt_loss="{item}">
                <td>
                    <DFormCheck
                        :id="`action_${item.id}_debt_loss`"
                        v-model="item.debt_loss"
                        inline
                        type="radio"
                        :name="item.id"
                        @change="onChangeActions(item, 'debt_loss')"
                    />
                </td>
            </template>
            <template #collection_and_debt_loss="{item}">
                <td>
                    <DFormCheck
                        :id="`action_${item.id}collection_and_debt_loss`"
                        v-model="item.collection_and_debt_loss"
                        inline
                        type="radio"
                        :name="item.id"
                        @change="onChangeActions(item, 'collection_and_debt_loss')"
                    />
                </td>
            </template>
            <template #ignore="{item}">
                <td>
                    <DFormCheck
                        :id="`action_${item.id}_ignore`"
                        v-model="item.ignore"
                        inline
                        type="radio"
                        :name="item.id"
                        @change="onChangeActions(item, 'ignore')"
                    />
                </td>
            </template>
            <template #id="{item}">
                <td v-if="userPermissions.includes('invoice.view')">
                    <Link :href="showInvoiceRoute(item)">
                        {{ item.id }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.id }}
                </td>
            </template>
            <template #customer_firstname="{item}">
                <td>{{ item.customer.firstname }}</td>
            </template>
            <template #customer_lastname="{item}">
                <td>{{ item.customer.lastname }}</td>
            </template>
            <template #customer_company="{item}">
                <td>{{ item.customer.company }}</td>
            </template>
            <template #brand="{item}">
                <td>{{ item.customer.brand }}</td>
            </template>
            <template #customer_id="{item}">
                <td v-if="userPermissions.includes('customer.view')">
                    <Link :href="customerInvoicesRoute(item)">
                        {{ item.customer.id }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.customer.id }}
                </td>
            </template>
            <template #total_amount="{item}">
                <td
                    class="text-right"
                    :class="{'table-text-color-red': item.total_amount < 0}"
                >
                    {{ priceFormatForDisplay(item.total_amount, 2, item.customer.market_region_id) }}
                </td>
            </template>
            <template #open_item_amount="{item}">
                <td
                    class="text-right"
                    :class="{'table-text-color-red': item.open_item_amount < 0}"
                >
                    {{ priceFormatForDisplay(item.open_item_amount, 2, item.customer.market_region_id) }}
                </td>
            </template>
            <template #sanity="{item}">
                <td>{{ __('customers.common.sanity.' + item.customer.sanity) }}</td>
            </template>
            <template #products="{item}">
                <td>
                    <div
                        v-for="(product, index) in item.products"
                        :key="index"
                    >
                        {{ product }}
                    </div>
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="pagination.last_page > 1"
            :active-page="pagination.current_page"
            :pages="pagination.last_page"
            align="center"
            @update:activePage="applyFilter"
        />
        <DFabs>
            <DFab
                id="btn-add"
                icon="file-export"
                :label="__('tools.collection.handover_collections')"
                @click="onClickHandover"
            />
        </DFabs>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
        <DModal
            v-model:show="showConfirmationModal"
            :title="__('tools.collection.modal_handover_collections_title')"
            is-info
            is-confirm
            is-big
            :overflowAuto="true"
            @confirm="handoverCollections"
            @close="showConfirmationModal = false"
        >
            <div>
                <p class="pt-0 ms-3 mt-3">
                    {{ __('tools.collection.modal_handover_collections_output') }}
                </p>
                <p
                    v-for="(item, key) in checkedActionsDisplayList"
                    :key="key"
                    class="pt-0 font-italic ms-3"
                >
                    {{ item }}
                </p>
            </div>
        </DModal>
        <DModal
            v-model:show="showMaxEntriesModal"
            :title="__('messages.error')"
            is-info
            @close="showMaxEntriesModal = false"
        >
            <p class="pt-0 ms-3 mt-3">
                {{ __('tools.collection.max_entries_exceeded', { max: maxCollectionEntries }) }}
            </p>
        </DModal>
    </DCard>
</template>

<script>
import App from '~@/App.vue'
import CollectionLayout from '~@/Pages/Tools/Collection/Layout.vue'
import CollectionFilter from '~@/Components/Tools/Collection/Filter.vue'
import priceFormat from '~@/Mixins/priceFormat'
import ListViewModal from '~@/Components/ListView/ListViewModal'

export default {
    name: 'CollectionNational',
    components: { CollectionFilter, ListViewModal },
    mixins: [
        priceFormat
    ],
    layout: [App, CollectionLayout],
    props: {
        invoices: {
            type: Array,
            default: () => { return [] }
        },
        appliedFilters: {
            type: Object,
            default: () => {
                return {
                    invoiceId: null,
                    customerId: null,
                    customerSearch: null,
                    invoicePriceOperator: 'greater',
                    invoicePrice: null,
                    brands: [],
                    excludeInvoicesWithOpenIncidents: false,
                    currency: null
                }
            }
        },
        checkedActions: {
            type: Object,
            default: () => { return {} }
        },
        headerCheckedActions: {
            type: Object,
            default: () => { return {} }
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        maxCollectionEntries: {
            type: Number,
            default: 250
        },
        isCollectionPartnerVisma: {
            type: Boolean,
            default: false
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        pagination: {
            type: Object,
            default: () => { return {} }
        },
        currencyCodes: {
            type: Array,
            default: () => { return [] }
        }
    },
    data () {
        return {
            exclusion: {},
            invoiceList: [...this.invoices],
            filter: {
                invoiceId: this.appliedFilters.invoiceId,
                customerId: this.appliedFilters.customerId,
                customerSearch: this.appliedFilters.customerSearch,
                invoicePrice: this.appliedFilters.invoicePrice,
                invoicePriceOperator: this.appliedFilters.invoicePriceOperator === undefined ? 'greater' : this.appliedFilters.invoicePriceOperator,
                availableActions: this.availableActions,
                brands: this.appliedFilters.brands ?? [],
                excludeInvoicesWithOpenIncidents: this.appliedFilters.excludeInvoicesWithOpenIncidents,
                currency: this.appliedFilters.currency
            },
            headerActions: {
                collection: this.headerCheckedActions['collection-header'] ?? false,
                debt_loss: this.headerCheckedActions['debt_loss-header'] ?? false,
                collection_and_debt_loss: this.headerCheckedActions['collection_and_debt_loss-header'] ?? false,
                ignore: this.headerCheckedActions['ignore-header'] ?? false
            },
            checkedActionsData: { ...this.checkedActions },
            checkedActionsSets: {
                collection: new Set(this.checkedActions.collection ?? []),
                debt_loss: new Set(this.checkedActions.debt_loss ?? []),
                collection_and_debt_loss: new Set(this.checkedActions.collection_and_debt_loss ?? []),
                ignore: new Set(this.checkedActions.ignore ?? []),
            },
            headerCheckedActionsData: this.headerCheckedActions,
            showListViewModal: false,
            showConfirmationModal: false,
            showMaxEntriesModal: false,
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        checkedActionsDisplayList () {
            return Object.entries(this.checkedActionsData)
                .filter(([key]) => key !== 'ignore')
                .map(([key, value]) =>
                    `${key.replaceAll('_', ' ')}: ${value.length ? value.join(', ') : '—'}`
                )
        }
    },
    watch: {
        invoices (next) {
            this.invoiceList = [...next]
        }
    },
    created () {
        this.setPreselectedActions()
    },
    methods: {
        onClickHandover () {
            const total = this.checkedActionsSets.collection.size + this.checkedActionsSets.debt_loss.size + this.checkedActionsSets.collection_and_debt_loss.size

            if (total > this.maxCollectionEntries) {
                this.showMaxEntriesModal = true

                return
            }

            this.showConfirmationModal = true
        },
        handoverCollections () {
            const invoicesForCollection = {}
            for (const [key, set] of Object.entries(this.checkedActionsSets)) {
                if (key === 'ignore') {
                    continue
                }
                if (set.size > 0) {
                    invoicesForCollection[key] = Array.from(set)
                }
            }

            this.$inertia.post(
                this.$route('tools.collection.handover'),
                {
                    invoices: invoicesForCollection
                },
                {
                    onError: () => {
                        this.setPreselectedActions()
                    },
                    onSuccess: () => {
                        this.setPreselectedActions()
                    }
                }
            )
        },
        getFields () {
            const availableList = [
                { key: 'collection', label: '', _style: { minWidth: '10%' } },
                { key: 'debt_loss', label: '', _style: { minWidth: '10%' } },
                { key: 'collection_and_debt_loss', label: '', _style: { minWidth: '10%' } },
                { key: 'ignore', label: '', _style: { minWidth: '10%' } },
            ]

            this.userListView.forEach((item) => {
                if (item.show_column === false) {
                    return
                }

                availableList.push({
                    key: item.column,
                    label: item.column !== 'actions' && item.column !== 'icon'
                        ? this.__('settings.list_view.' + item.list + '.' + item.column)
                        : '',
                    sorter: item.sort_column_enabled,
                    _style: item.width !== null
                        ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' }
                        : {}
                })
            })

            return availableList
        },
        showInvoiceRoute (invoice) {
            return this.$route('customers.customers.invoices.show', {
                customer: invoice.customer.id,
                invoice: invoice.id
            })
        },
        customerInvoicesRoute (invoice) {
            return this.$route('customers.customers.invoices.index', { customer: invoice.customer.id })
        },
        radioButtonsData () {
            const radioButtonsData = {
                collection: [],
                debt_loss: [],
                collection_and_debt_loss: [],
                ignore: [],
                'collection-header': 'false',
                'debt_loss-header': 'false',
                'collection_and_debt_loss-header': 'false',
                'ignore-header': 'false'
            }

            for (const [key, arrayOfIds] of Object.entries(this.checkedActionsData)) {
                radioButtonsData[key] = arrayOfIds
            }

            for (const [key, value] of Object.entries(this.headerActions)) {
                radioButtonsData[`${key}-header`] = value === true ? 'true' : 'false'
            }

            return radioButtonsData
        },
        applyFilter (page) {
            const queryParameters = Object.fromEntries(Object.entries(this.filter).filter(([_, v]) => v))

            if (page !== 1) {
                queryParameters._query = { page: page }
            }

            queryParameters.save_filters = true

            this.$inertia.post(
                this.$route('tools.collection.national', {
                    _query: queryParameters,
                    saveFilters: true,
                    defaultFilters: false,
                    page: page,
                }),
                this.radioButtonsData(),
                {
                    preserveScroll: false,
                    preserveState: true,
                    onSuccess: () => {
                        this.setPreselectedActions()
                        // The list scroll lives inside .table-sticky-header (inner scroller),
                        // not on .d-main, so reset that container's scroll position.
                        this.$el.querySelector('.table-sticky-header')?.scrollTo({ top: 0, behavior: 'auto' })
                    }
                }
            )
        },
        onChangeActions (activeInvoice, activeAction) {
            const dirtyKeys = new Set()

            for (const key of Object.keys(this.checkedActionsSets)) {
                const shouldBeChecked = key === activeAction
                if (activeInvoice[key] !== shouldBeChecked) {
                    activeInvoice[key] = shouldBeChecked
                }
                if (shouldBeChecked) {
                    if (!this.checkedActionsSets[key].has(activeInvoice.id)) {
                        this.checkedActionsSets[key].add(activeInvoice.id)
                        dirtyKeys.add(key)
                    }
                } else if (this.checkedActionsSets[key].delete(activeInvoice.id)) {
                    dirtyKeys.add(key)
                }
            }

            for (const key of dirtyKeys) {
                this.syncCheckedActionForKey(key)
            }

            for (const key of Object.keys(this.headerActions)) {
                if (this.headerActions[key]) {
                    this.headerActions[key] = false
                }
            }
        },
        onClickAllActions (action) {
            const newValue = !this.headerActions[action]

            // Cannot uncheck the "ignore" header
            if (action === 'ignore' && newValue === false) {
                return
            }

            for (const key of Object.keys(this.headerActions)) {
                this.headerActions[key] = key === action ? newValue : false
            }

            const ignoreOnUncheck = newValue === false
            for (const invoice of this.invoiceList) {
                invoice.collection = !ignoreOnUncheck && action === 'collection'
                invoice.debt_loss = !ignoreOnUncheck && action === 'debt_loss'
                invoice.collection_and_debt_loss = !ignoreOnUncheck && action === 'collection_and_debt_loss'
                invoice.ignore = ignoreOnUncheck || action === 'ignore'

                // Only mutate current-page IDs in the sets so off-page selections survive.
                for (const key of Object.keys(this.checkedActionsSets)) {
                    const shouldBeChecked = (newValue === true && key === action) ||
                        (ignoreOnUncheck && key === 'ignore')
                    if (shouldBeChecked) {
                        this.checkedActionsSets[key].add(invoice.id)
                    } else {
                        this.checkedActionsSets[key].delete(invoice.id)
                    }
                }
            }

            this.syncCheckedActionsData()
        },
        syncCheckedActionForKey (key) {
            this.checkedActionsData[key] = Array.from(this.checkedActionsSets[key])
        },
        syncCheckedActionsData () {
            this.checkedActionsData = {
                collection: Array.from(this.checkedActionsSets.collection),
                debt_loss: Array.from(this.checkedActionsSets.debt_loss),
                collection_and_debt_loss: Array.from(this.checkedActionsSets.collection_and_debt_loss),
                ignore: Array.from(this.checkedActionsSets.ignore),
            }
        },
        setPreselectedActions () {
            this.checkedActionsData = {
                collection: this.checkedActions.collection ?? [],
                debt_loss: this.checkedActions.debt_loss ?? [],
                collection_and_debt_loss: this.checkedActions.collection_and_debt_loss ?? [],
                ignore: this.checkedActions.ignore ?? [],
            }
            this.checkedActionsSets = {
                collection: new Set(this.checkedActionsData.collection),
                debt_loss: new Set(this.checkedActionsData.debt_loss),
                collection_and_debt_loss: new Set(this.checkedActionsData.collection_and_debt_loss),
                ignore: new Set(this.checkedActionsData.ignore),
            }

            this.invoiceList.forEach((invoice) => {
                let checkIgnore = true
                for (const key of Object.keys(this.checkedActionsSets)) {
                    const isChecked = this.checkedActionsSets[key].has(invoice.id)
                    invoice[key] = isChecked
                    if (isChecked) {
                        checkIgnore = false
                    }
                }
                if (checkIgnore) {
                    invoice.ignore = true
                }
            })

            this.headerActions.collection = this.headerCheckedActions['collection-header']
            this.headerActions.debt_loss = this.headerCheckedActions['debt_loss-header']
            this.headerActions.collection_and_debt_loss = this.headerCheckedActions['collection_and_debt_loss-header']
            this.headerActions.ignore = this.headerCheckedActions['ignore-header']
        }
    }
}
</script>

<style lang="scss" scoped>
.header-actions {
    display: grid;
    justify-items: flex-start;
    gap: 10px;
}

// Make the inner scroller cover most of the viewport so pagination ends up
// near the bottom rather than mid-screen, and contain horizontal overflow
// inside the scroller (.table-responsive is forced to overflow:visible
// globally in app.scss, which lets wide tables escape .d-main otherwise).
// .table-responsive is forced to overflow:visible!important globally in
// app.scss, which would otherwise let the table escape this scroller.
// Beat it with !important so the inner scroller actually clips on both axes.
:deep(.table-sticky-header) {
    max-height: calc(100vh - 240px);
    overflow-x: auto !important;
    overflow-y: auto !important;
}
</style>
